import React from 'react';
import Box from "@amzn/meridian/box";
import MetricCard from '../MetricCard';
import { OverrideMetrics, PrimaryGradingVolumeData } from "../../pages/reviewAndPublish/model";
import { EnhancedShiftTotals } from "../../pages/reviewAndPublish/model";
import {
  TOOLTIP_RCC_RECOMMENDED_VOLUME,
  TOOLTIP_ALPS_PLANNED_VOLUME,
  TOOLTIP_OVERRIDDEN_VOLUME,
  TOOLTIP_LABOR_HOURS_DELTA,
} from "../../i18n/tooltips";
import './TopMetricsRow.scss';

export interface TopMetricsRowStateProps {
    primaryGradingVolumeData?: PrimaryGradingVolumeData,
    totalOverrides?: OverrideMetrics,
    shiftTotals?: EnhancedShiftTotals
}

const TopMetricsRow: React.FC<TopMetricsRowStateProps> = ({
  primaryGradingVolumeData,
  totalOverrides,
  shiftTotals
}) => {
  const primaryGradingRecommendedDiff: number | undefined =
    primaryGradingVolumeData?.recommended?.total !== undefined &&
    primaryGradingVolumeData?.recommended?.alps !== undefined
      ? primaryGradingVolumeData.recommended.total - primaryGradingVolumeData.recommended.alps
      : undefined;

  const primaryGradingOverrideDiff: number | undefined =
    primaryGradingVolumeData?.override?.total !== undefined &&
    primaryGradingVolumeData?.override?.alps !== undefined
      ? primaryGradingVolumeData.override.total - primaryGradingVolumeData.override.alps
      : undefined;

  const overridesTotalDiff: number | undefined =
    totalOverrides?.total !== undefined &&
    totalOverrides?.average !== undefined
      ? totalOverrides.total - totalOverrides.average
      : undefined;

  // Check if override is provided (not null, undefined, or zero)
  const hasOverride = primaryGradingVolumeData?.override?.total !== undefined && 
                     primaryGradingVolumeData?.override?.total !== null &&
                     primaryGradingVolumeData?.override?.total > 0;

  // Labor Hours Delta calculation
  const laborHoursValue = shiftTotals?.showHoursNeeded?.intValue;
  const laborHoursDisplayValue = laborHoursValue
    ? Math.abs(laborHoursValue)
    : '--';
  const laborHoursSuffix = laborHoursValue
    ? (laborHoursValue > 0 ? 'lower' : 'higher')
    : '';
  const laborHoursRequirement = hasOverride
    ? 'overridden volume requirement'
    : 'ALPS requirement';
  const laborHoursUnit = laborHoursValue
    ? `labor hours ${laborHoursSuffix} than ${laborHoursRequirement}`
    : 'labor hours vs ALPS';

  return (
    <Box className="top-metrics-row">
      <MetricCard
        title="RCC Recommended Primary Grading Volume"
        value={primaryGradingVolumeData?.recommended?.total ?? "--"}
        unit={"units"}
        valueDiff={primaryGradingRecommendedDiff}
        subtext={"units vs. ALPS target"}
        subtextColor={primaryGradingRecommendedDiff ? (primaryGradingRecommendedDiff > 0 ? "success" : "error") : undefined}
        className="top-metrics-row__card"
        tooltip={TOOLTIP_RCC_RECOMMENDED_VOLUME}
        titleColor="primary"
        valueColor="primary"
      />

      <MetricCard
        title="ALPS Planned Volume"
        value={primaryGradingVolumeData?.recommended?.alps ?? "--"}
        unit={"units"}
        className="top-metrics-row__card"
        tooltip={TOOLTIP_ALPS_PLANNED_VOLUME}
      />

      {hasOverride && (
        <MetricCard
          title="Overridden Primary Grading Volume"
          value={primaryGradingVolumeData.override.total}
          unit={"units"}
          valueDiff={primaryGradingOverrideDiff}
          subtext={"units vs. ALPS target"}
          subtextColor={primaryGradingOverrideDiff ? (primaryGradingOverrideDiff > 0 ? "success" : "error") : undefined}
          valueColor="primary"
          className="top-metrics-row__card"
          tooltip={TOOLTIP_OVERRIDDEN_VOLUME}
        />
      )}

      <MetricCard
        title="Labor Hours Delta"
        value={laborHoursDisplayValue}
        unit={laborHoursUnit}
        className="top-metrics-row__card"
        tooltip={TOOLTIP_LABOR_HOURS_DELTA}
        titleColor="primary"
        valueColor="primary"
      />
    </Box>
  );
};

export default TopMetricsRow;
